import { getColumnPreferences, saveColumnPreferences, resetColumnPreferences } from '../services/api';
import { createLogger } from './debugLogger';

// Create debug logger for this module
const logger = createLogger('columnPreferences', 'Table column preferences and visibility management', false, 'INFO');

// System default configurations (fallback when no user preferences exist)
// NOTE: Keep in sync with backend/app/api/column_preferences.py SYSTEM_DEFAULTS map.
const SYSTEM_DEFAULTS = {
  account_details: {
    visibleColumns: {
      strategy: true,
      notes: true,
      rating: true,
      entry: true,
      tsize: true,
      next: true,
      source: false,
      name: false,
      category: false,
      theme: true,
      base: true,
      shares: true,
      cost_basis: false,
      total_cost: false,
      pre_close: false,
      current_price: false,
      current_value: true,
      high: true,
      today_gain_amount: false,
      today_gain_percentage: true,
      total_gain: false,
      total_gain_pct: true,
      trade_gain_pct: true,
      days_held: true,
      position_size: true,
      armor: true,
      risk_gain: true,
      trisk_gain: true,
      risk_gain_amount: true,
      actions: true
    },
    columnOrder: [
      'symbol',
      'strategy',
      'actions',
      'notes',
      'name',
      'rating',
      'entry',
      'shares',
      'cost_basis',
      'total_cost',
      'current_price',
      'pre_close',
      'current_value',
      'high',
      'today_gain_amount',
      'today_gain_percentage',
      'total_gain',
      'total_gain_pct',
      'trade_gain_pct',
      'days_held',
      'position_size',
      'tsize',
      'next',
      'source',
      'category',
      'theme',
      'base',
      'armor',
      'risk_gain',
      'trisk_gain',
      'risk_gain_amount'
    ]
  },
  tickers: {
    visibleColumns: {
      strategy: true,
      name: false,
      rating: true,
      source: true,
      category: true,
      theme: true,
      base: false,
      entry: true,
      shares: true,
      cost_basis: false,
      total_cost: false,
      pre_close: false,
      current_price: false,
      current_value: true,
      high: true,
      today_gain_amount: false,
      today_gain_percentage: true,
      total_gain: false,
      total_gain_pct: true,
      trade_gain: true,
      trade_gain_pct: true,
      days_held: true,
      position_size: true,
      armor: true,
      risk_gain: true,
      risk_gain_amount: true,
      actions: true
    },
    columnOrder: [
      'symbol',
      'strategy',
      'actions',
      'name',
      'rating',
      'source',
      'category',
      'theme',
      'base',
      'entry',
      'shares',
      'cost_basis',
      'total_cost',
      'current_price',
      'pre_close',
      'current_value',
      'high',
      'today_gain_amount',
      'today_gain_percentage',
      'total_gain',
      'total_gain_pct',
      'trade_gain',
      'trade_gain_pct',
      'days_held',
      'position_size',
      'armor',
      'risk_gain',
      'risk_gain_amount'
    ]
  },
  all_accounts: {
    visibleColumns: {
      portfolio: true,
      portfolioInfo: true,
      description: true,
      balance: true,
      cash: true,
      edge: true,
      today_gain_amount: true,
      today_gain_percentage: true,
      total_gain_amount: true,
      total_gain_percentage: true,
      ytd_gain_amount: true,
      ytd_gain_percentage: true
    },
    columnOrder: [
      'portfolio',
      'portfolioInfo',
      'description',
      'balance',
      'cash',
      'edge',
      'today_gain_amount',
      'today_gain_percentage',
      'total_gain_amount',
      'total_gain_percentage',
      'ytd_gain_amount',
      'ytd_gain_percentage'
    ]
  }
};

/**
 * Get default column configuration for a specific page type
 * First tries to fetch user's saved defaults from backend,
 * falls back to system defaults if none exist
 * @param {string} pageType - The page type (e.g., 'account_details')
 * @returns {Promise<Object>} Object containing visibleColumns and columnOrder
 */
export const getDefaultColumnConfig = async (pageType) => {
  try {
    const response = await getColumnPreferences(pageType);
    return {
      visibleColumns: response.data.visible_columns,
      columnOrder: response.data.column_order,
      isCustom: response.data.is_custom
    };
  } catch (error) {
    logger.warn(`Failed to load column preferences for ${pageType}, using system defaults:`, error);
    
    // Fallback to system defaults
    const systemDefault = SYSTEM_DEFAULTS[pageType] || {
      visibleColumns: {},
      columnOrder: []
    };
    
    return {
      visibleColumns: systemDefault.visibleColumns,
      columnOrder: systemDefault.columnOrder,
      isCustom: false
    };
  }
};

/**
 * Save current column configuration as user's default
 * @param {string} pageType - The page type (e.g., 'account_details')
 * @param {Object} visibleColumns - Current visible columns configuration
 * @param {Array} columnOrder - Current column order configuration
 * @returns {Promise<void>}
 */
export const saveAsDefaultColumnConfig = async (pageType, visibleColumns, columnOrder) => {
  try {
    await saveColumnPreferences(pageType, visibleColumns, columnOrder);
    logger.info(`Saved column preferences as default for ${pageType}`);
  } catch (error) {
    logger.error(`Failed to save column preferences for ${pageType}:`, error);
    throw error;
  }
};

/**
 * Reset column configuration to system defaults
 * This will delete user's custom preferences and return system defaults
 * @param {string} pageType - The page type (e.g., 'account_details')
 * @returns {Promise<Object>} Object containing visibleColumns and columnOrder
 */
export const resetToDefaultColumnConfig = async (pageType) => {
  try {
    logger.info(`Calling resetColumnPreferences API for ${pageType}`);
    const response = await resetColumnPreferences(pageType);
    logger.debug('Reset API response:', response.data);
    return {
      visibleColumns: response.data.visible_columns,
      columnOrder: response.data.column_order,
      isCustom: false
    };
  } catch (error) {
    logger.error(`Failed to reset column preferences for ${pageType}:`, error);
    
    // Fallback to system defaults
    const systemDefault = SYSTEM_DEFAULTS[pageType] || {
      visibleColumns: {},
      columnOrder: []
    };
    
    return {
      visibleColumns: systemDefault.visibleColumns,
      columnOrder: systemDefault.columnOrder,
      isCustom: false
    };
  }
};

/**
 * Initialize column configuration for a component
 * This function should be called during component initialization to load
 * the user's saved preferences or fall back to defaults
 * @param {string} pageType - The page type (e.g., 'account_details') 
 * @param {string} accountId - Account ID for localStorage fallback (optional)
 * @returns {Promise<Object>} Object containing visibleColumns and columnOrder
 */
export const initializeColumnConfig = async (pageType, accountId = null) => {
  try {
    // First try to get user's saved defaults from backend
    const defaultConfig = await getDefaultColumnConfig(pageType);
    
    // If we have custom defaults, use them
    if (defaultConfig.isCustom) {
      return defaultConfig;
    }
    
    // If no custom defaults, check if there are localStorage settings for this specific account
    if (accountId) {
      const savedColumns = localStorage.getItem(`visibleColumns-account-${accountId}`);
      const savedOrder = localStorage.getItem(`columnOrder-account-${accountId}`);
      
      if (savedColumns && savedOrder) {
        try {
          return {
            visibleColumns: JSON.parse(savedColumns),
            columnOrder: JSON.parse(savedOrder),
            isCustom: false // These are account-specific, not user defaults
          };
        } catch (e) {
          logger.warn('Failed to parse localStorage column settings, using defaults');
        }
      }
    }

    // Fall back to system defaults
    return defaultConfig;

  } catch (error) {
    logger.error(`Failed to initialize column config for ${pageType}:`, error);
    
    // Final fallback to system defaults
    const systemDefault = SYSTEM_DEFAULTS[pageType] || {
      visibleColumns: {},
      columnOrder: []
    };
    
    return {
      visibleColumns: systemDefault.visibleColumns,
      columnOrder: systemDefault.columnOrder,
      isCustom: false
    };
  }
};

/**
 * Get system default configuration for a page type
 * @param {string} pageType - The page type (e.g., 'account_details')
 * @returns {Object} System default configuration
 */
export const getSystemDefaultConfig = (pageType) => {
  const systemDefault = SYSTEM_DEFAULTS[pageType] || {
    visibleColumns: {},
    columnOrder: []
  };
  
  return {
    visibleColumns: { ...systemDefault.visibleColumns },
    columnOrder: [...systemDefault.columnOrder],
    isCustom: false
  };
};
